import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { Dispatch, SetStateAction } from 'react';
import { AlertCircle } from 'lucide-react';
import { DataTable, Pagination, PaginationState } from '@/components';
import type { EfaxQueueItem } from '../types';
import { EfaxEmptyState } from './EfaxEmptyState';

interface EfaxQueueTableProps {
  columns: ColumnDef<EfaxQueueItem>[];
  hasSearched: boolean;
  isFetching?: boolean;
  isError: boolean;
  items: EfaxQueueItem[];
  totalCount: number;
  sorting: SortingState;
  onSortingChange: Dispatch<SetStateAction<SortingState>>;
  pagination: PaginationState;
  setPagination: Dispatch<SetStateAction<PaginationState>>;
}

export const EfaxQueueTable = ({
  columns,
  hasSearched,
  isFetching = false,
  isError,
  items,
  totalCount,
  sorting,
  onSortingChange,
  pagination,
  setPagination,
}: EfaxQueueTableProps) => {
  if (!hasSearched) {
    return <EfaxEmptyState hasSearched={false} />;
  }

  if (isError && !isFetching) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="max-w-sm text-center">
          <div className="bg-destructive/10 text-destructive mx-auto mb-4 flex size-12 items-center justify-center rounded-full border">
            <AlertCircle className="size-5" />
          </div>
          <h2 className="text-lg font-semibold">
            Unable to load eFax documents
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Something went wrong while fetching results. Please try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <DataTable
        columns={columns}
        data={items}
        isLoading={isFetching}
        pageSize={pagination.pageSize}
        sorting={sorting}
        onSortingChange={onSortingChange}
        emptyState={<EfaxEmptyState hasSearched={true} />}
      />
      {totalCount > 0 ? (
        <div className="border-t p-3">
          <Pagination
            pagination={pagination}
            setPagination={setPagination}
            totalCount={totalCount}
            pageSizeOptions={[10]}
          />
        </div>
      ) : null}
    </>
  );
};
